import { Anomaly } from '@api/twin/AnomalyApi';
import { AttachmentApi, AttachmentTargetType } from '@api/twin/AttachmentApi';
import { Comment } from '@api/twin/CommentApi';
import { useTeiaViewerContext, useTwinSource } from '@stereograph/teiaviewer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePluginTranslation } from '@translation';
import DOMPurify from 'dompurify';
import { useAnomalySectionContext } from '../context/AnomalySectionContext';
import { anomalyQueryKeys } from '../query-keys';

type AnomalyAttachmentTargetType = Extract<AttachmentTargetType, 'Anomaly' | 'Comment'>;

interface UseUploadAnomalyDocumentMutationParams {
  document: File;
  targetId: number;
  targetType: AnomalyAttachmentTargetType;
}

export const useUploadAnomalyDocument = () => {
  const { viewer } = useTeiaViewerContext();
  const { twinApiClient } = viewer;
  const { t } = usePluginTranslation();
  const twin = useTwinSource();
  const queryClient = useQueryClient();
  const { anomaly } = useAnomalySectionContext();

  const { mutateAsync } = useMutation({
    mutationFn: (params: UseUploadAnomalyDocumentMutationParams) => {
      return AttachmentApi(twinApiClient).post(
        twin.projectId,
        params.targetId,
        params.targetType,
        params.document,
      );
    },
    onMutate: () => {
      return viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'pending',
        message: t('anomaly_plugin.document.upload.notification.loading_text'),
        title: t('anomaly_plugin.document.upload.notification.notification_title'),
        displayed: true,
      });
    },
    onSuccess: async (_, variables, notificationId) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.documents(anomaly.id),
        }),
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.commentDocuments(variables.targetId),
        }),
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.history_default(anomaly.id),
        }),
      ]);

      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'success',
        message: t('anomaly_plugin.document.upload.notification.success_text', {
          document: DOMPurify.sanitize(variables.document.name),
        }),
        title: t('anomaly_plugin.document.upload.notification.notification_title'),
        displayed: true,
      }, notificationId);
    },
    onError: (_, variables, notificationId) => {
      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'error',
        message: t('anomaly_plugin.document.upload.notification.error_text', {
          document: DOMPurify.sanitize(variables.document.name),
        }),
        title: t('anomaly_plugin.document.upload.notification.notification_title'),
        displayed: true,
      }, notificationId);
    },
  });

  const mutateAnomaly = (anomaly: Anomaly, documents: Array<File>) => {
    return Promise.all(
      Array.from(documents).map((document) =>
        mutateAsync({
          document,
          targetId: anomaly.id,
          targetType: 'Anomaly',
        }),
      ),
    );
  };

  const mutateComment = (comment: Comment, documents: Array<File>) => {
    return Promise.all(
      Array.from(documents).map((document) =>
        mutateAsync({
          document,
          targetId: comment.id,
          targetType: 'Comment',
        }),
      ),
    );
  };

  return {
    uploadToAnomaly: mutateAnomaly,
    uploadToComment: mutateComment,
  };
};
